import { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const navLinks = [
  { label: 'Home', to: '/', end: true },
  { label: 'About', to: '/about' },
  { label: 'Capabilities', to: '/capabilities' },
  { label: 'Payroll & Compliance', to: '/payroll-compliance' },
  { label: 'Industries', to: '/industries' },
  { label: 'Innovation', to: '/innovation' },
  { label: 'Capital Partners', to: '/capital-partners' },
  { label: 'Client Portal', to: '/client-portal' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'backdrop-blur-xl bg-white/90 shadow-[0_1px_20px_-4px_rgba(26,34,52,0.08)]'
          : 'bg-white/95 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <NavLink to="/" end data-testid="nav-logo" className="flex flex-col leading-none group">
            <span className="logo-text text-[#1A2234] text-[17px] tracking-wider group-hover:text-[#0ABFBC] transition-colors duration-200">
              BOSFED®
            </span>
            <span className="text-[9px] text-[#475569] tracking-[0.2em] uppercase font-semibold">
              Boston Federal
            </span>
          </NavLink>

          {/* Desktop Navigation */}
          <div className="hidden xl:flex items-center gap-5">
            {navLinks.map(link => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.end}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-').replace(/[&]/g, '').replace(/--/g, '-')}`}
                className={({ isActive }) =>
                  `text-[13px] font-medium transition-colors duration-200 relative ${
                    isActive
                      ? 'text-[#0ABFBC]'
                      : 'text-[#475569] hover:text-[#0ABFBC]'
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden xl:block">
            <button
              data-testid="nav-contact-cta"
              onClick={() => navigate('/contact')}
              className="bg-[#0ABFBC] text-white px-5 py-2 rounded-md text-sm font-semibold hover:bg-[#09A09D] transition-colors duration-300"
            >
              Contact Us
            </button>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            data-testid="nav-mobile-menu-toggle"
            onClick={() => setMenuOpen(!menuOpen)}
            className="xl:hidden p-2 text-[#1A2234] hover:text-[#0ABFBC] transition-colors"
            aria-label="Toggle menu"
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="xl:hidden bg-white border-t border-slate-100 px-4 py-4 shadow-lg">
          <div className="space-y-1">
            {navLinks.map(link => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.end}
                onClick={() => setMenuOpen(false)}
                data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, '-').replace(/[&]/g, '').replace(/--/g, '-')}`}
                className={({ isActive }) =>
                  `block py-2.5 px-3 rounded-md text-sm font-medium transition-colors ${
                    isActive ? 'text-[#0ABFBC] bg-[#0ABFBC]/5' : 'text-[#1A2234] hover:bg-slate-50'
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-slate-100">
            <button
              data-testid="mobile-contact-cta"
              onClick={() => { navigate('/contact'); setMenuOpen(false); }}
              className="w-full bg-[#0ABFBC] text-white py-2.5 rounded-md text-sm font-semibold hover:bg-[#09A09D] transition-colors"
            >
              Contact Us
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}
